import React, { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Home, Phone, ShoppingBag, ShoppingCart, UserRound } from 'lucide-react';
import { cn } from '../utils/cn';
import { logoUrl, telHref, useSite } from '../utils/site';
import { cartCount, shopApi, useCart } from './shopApi';

// The online shop's own frame - like a phone app: a slim top bar and, on a
// phone, tabs along the bottom (Home, Shop, Cart, Orders).
const TABS = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/shop', label: 'Shop', icon: ShoppingBag, end: true },
  { to: '/shop/cart', label: 'Cart', icon: ShoppingCart, end: false },
  { to: '/shop/orders', label: 'Orders', icon: ClipboardList, end: false },
  { to: '/shop/account', label: 'Account', icon: UserRound, end: false }, // subscription customers (mobile + PIN)
];

export function useShopFront() {
  return useQuery({ queryKey: ['shop-front'], queryFn: shopApi.front, staleTime: 60_000 });
}

export default function ShopLayout() {
  const site = useSite();
  const count = cartCount(useCart());
  const { pathname } = useLocation();
  const front = useShopFront().data;
  useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);

  return (
    <div className="min-h-screen bg-surface-subtle pb-20 md:pb-0">
      <header className="sticky top-0 z-30 border-b border-brand-green-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <Link to="/shop" className="flex min-w-0 items-center gap-2">
            <img src={logoUrl(site)} alt="" className="h-9 w-9 shrink-0 object-contain" />
            <span className="min-w-0 leading-tight">
              <span className="block truncate font-bold text-brand-green-900">Sri KalpaGavya</span>
              <span className="block truncate text-[11px] text-ink-400">Fresh milk, delivered</span>
            </span>
          </Link>
          <nav className="ml-auto hidden items-center gap-1 md:flex">
            {TABS.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end}
                className={({ isActive }) => cn('relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium',
                  isActive && t.to !== '/' ? 'bg-brand-green-50 text-brand-green-800' : 'text-ink-500 hover:text-brand-green-700')}>
                <t.icon className="h-4 w-4" /> {t.label}
                {t.label === 'Cart' && count > 0 && <span className="rounded-full bg-brand-gold-500 px-1.5 text-[11px] font-bold text-white">{count}</span>}
              </NavLink>
            ))}
          </nav>
          {site.phone && (
            <a href={telHref(front?.support_phone || site.phone)} className="ml-auto inline-flex items-center gap-1 rounded-full border border-brand-green-200 px-3 py-1.5 text-xs font-semibold text-brand-green-800 md:ml-2">
              <Phone className="h-3.5 w-3.5" /> Call
            </a>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-4">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-brand-green-100 bg-white pb-[env(safe-area-inset-bottom)] md:hidden" aria-label="Shop">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end}
            className={({ isActive }) => cn('relative flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium',
              isActive && t.to !== '/' ? 'text-brand-green-700' : 'text-ink-400')}>
            <t.icon className="h-5 w-5" />
            {t.label}
            {t.label === 'Cart' && count > 0 && (
              <span className="absolute right-[calc(50%-18px)] top-1 min-w-4 rounded-full bg-brand-gold-500 px-1 text-center text-[10px] font-bold leading-4 text-white">{count}</span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
